import { SerializedStyles, Theme, css } from '@emotion/react';
import { isSomething } from '@utils/isSomething';
import { CSSProperties, ComponentProps } from 'react';

import { ButtonStylingProps } from './Button';

export type LinkButtonProps = ButtonStylingProps & ComponentProps<'link'>;

type GetColorsProps = {
    theme: Theme;
    colorScheme: ButtonStylingProps['colorScheme'];
    variant: ButtonStylingProps['variant'];
    color: CSSProperties['color'];
    altColor: CSSProperties['color'];
};

type GetDisplayInfo = {
    variant: ButtonStylingProps['variant'];
    variation: ButtonStylingProps['variation'];
    theme: Theme;
    colorScheme: ButtonStylingProps['colorScheme'];
};

type GetCursorProps = {
    isLoading?: boolean;
    disabled?: boolean;
};

type GetSizesProps = {
    theme: Theme;
    isFullWidth: ButtonStylingProps['isFullWidth'];
    buttonSize: ButtonStylingProps['buttonSize'];
    px: ButtonStylingProps['px'];
};

const getColors = ({
    theme,
    colorScheme,
    variant,
    color,
    altColor,
}: GetColorsProps): CSSProperties => {
    switch (variant) {
        case 'ghost':
        case 'underline':
        case 'outline':
            return {
                backgroundColor:
                    colorScheme === 'blackAlpha' || colorScheme === 'whiteAlpha'
                        ? theme.palette[colorScheme][400]
                        : 'transparent',
                color: color,
            };
        case 'solid':
            return {
                backgroundColor: color,
                color: altColor,
            };

        default:
            return {
                backgroundColor: 'white',
                color: altColor,
            };
    }
};

const getSizes = ({ theme, isFullWidth, buttonSize, px }: GetSizesProps) => {
    const common = {
        width: isFullWidth ? '100%' : 'fit-content',
    };

    switch (buttonSize) {
        case 'xsmall':
            return {
                ...common,
                paddingInline: isSomething(px) ? px : theme.spacing.xxs,
                minHeight: theme.spacing.md,
                fontSize: theme.typography.caption.fontSize,
                lineHeight: theme.typography.caption.lineHeight,
            };
        case 'small':
            return {
                ...common,
                paddingInline: isSomething(px) ? px : theme.spacing.xs,
                minHeight: theme.spacing.lg,
                fontSize: theme.typography.caption.fontSize,
                lineHeight: theme.typography.caption.lineHeight,
            };
        case 'medium':
            return {
                ...common,
                paddingInline: isSomething(px) ? px : theme.spacing.sm,
                minHeight: theme.spacing.xl,
                fontSize: theme.typography.body.fontSize,
                lineHeight: theme.typography.body.lineHeight,
            };
        case 'large':
            return {
                ...common,
                paddingInline: isSomething(px) ? px : theme.spacing.md,
                minHeight: theme.spacing.xxl,
                fontSize: theme.typography.heading2.fontSize,
                lineHeight: theme.typography.heading2.lineHeight,
            };
    }
};

const getCursor = ({ isLoading, disabled }: GetCursorProps): CSSProperties => {
    if (isLoading) {
        return { cursor: 'wait' };
    }

    if (disabled) {
        return { cursor: 'not-allowed' };
    }

    return { cursor: 'pointer' };
};

const getDisplayInfo = ({
    variant,
    variation,
    theme,
    colorScheme,
}: GetDisplayInfo) => {
    const isPrimaryVariation = variation === 'primary';
    const isSecondaryVariation = variation === 'secondary';
    const isUndefinedVariation = variation === undefined;
    const isOutlined = variant === 'outline';
    const isUnderlined = variant === 'underline';
    const isText = variant === 'ghost';
    const isBoxShadow = isPrimaryVariation && !isUnderlined && !isText;
    return {
        isPrimaryVariation,
        isSecondaryVariation,
        isUndefinedVariation,
        isOutlined,
        isUnderlined,
        isText,
        isBoxShadow,
        color:
            colorScheme === 'black'
                ? theme.palette.primary.main
                : theme.palette.secondary.main,
        altColor:
            colorScheme === 'black'
                ? theme.palette.primary.contrastText
                : theme.palette.secondary.contrastText,
    };
};

const getBorders = (isOutlined: boolean): CSSProperties => {
    if (isOutlined) {
        return {
            border: '1px solid currentColor',
        };
    } else {
        return {
            border: 'none',
        };
    }
};

export const getButtonStyle = (
    props: ButtonStylingProps & { theme: Theme }
): SerializedStyles => {
    const {
        buttonSize = 'medium',
        colorScheme = 'black',
        theme,
        variant = 'solid',
        variation,
        isLoading,
        disabled,
        isFullWidth,
        rounded,
        px,
    } = props;

    const { isOutlined, isUnderlined, isBoxShadow, color, altColor } =
        getDisplayInfo({
            variant,
            variation,
            theme,
            colorScheme,
        });

    return css({
        ...(theme.typography.button as CSSProperties),
        gap: '1rem',
        alignItems: 'center',
        overflow: 'hidden',
        boxSizing: 'border-box',
        display: 'inline-flex',
        justifyContent: 'center',
        pointerEvents: 'auto',
        position: 'relative',
        borderRadius:
            rounded === 'full' ? '9999px' : rounded || theme.shape.radius.xs,
        boxShadow: isBoxShadow ? theme.shadows.default : 'none',
        opacity: isLoading ? 0.8 : 1,
        textDecoration: isUnderlined ? 'underline' : 'none',
        ...getBorders(isOutlined),
        ...getColors({ theme, colorScheme, variant, color, altColor }),
        ...getCursor({ isLoading, disabled }),
        ...getSizes({ theme, isFullWidth, buttonSize, px }),
        ':first-letter': { textTransform: 'capitalize' },
        ':disabled': {
            backgroundColor: theme.palette.actions.disabled,
        },
    });
};

export const getRippleColor = ({
    variant,
    colorScheme,
}: {
    variant: ButtonStylingProps['variant'];
    colorScheme?: ButtonStylingProps['colorScheme'];
}) => {
    switch (variant) {
        case 'underline':
        case 'ghost':
        case 'outline':
            return 'rgba(0, 0, 0, 0.3)';

        case 'solid':
        default:
            if (!colorScheme || colorScheme === 'black') {
                return 'rgba(255, 255, 255, 0.3)';
            } else {
                return 'rgba(0, 0, 0, 0.15)';
            }
    }
};
